import _ from 'lodash';
import { logger } from '@fiverr-private/obs';
import { sendEvents } from '@fiverr-private/futile/lib/send/sendEvents';
import mixpanelService from '../../../shared/services/mixPanel';
import { SELECTORS } from '../config/filters';
import removeEmptyEntries from '../../../shared/utils/removeEmptyEntries';
import { EVENTS_MAPPING, BI_CLICK_DATA, FILTERS_LOCATION, BI_CONSTS } from '../utils/constants';
import localStorage from './localStorage';

const basePayload = ({ pageName, categoryIds, categoryName, subCategoryName, nestedSubCategoryName }) => ({
    'Page Name': pageName,
    'Category Name': categoryName,
    'Sub Category Name': subCategoryName,
    'Nested Sub Category Name': nestedSubCategoryName,
    ...categoryIds
});

const prepareMixpanelTrackPayload = ({ pageName, categoryIds, categoryName, subCategoryName, nestedSubCategoryName, payload = {} }) => removeEmptyEntries({
    ...basePayload({ pageName, categoryIds, categoryName, subCategoryName, nestedSubCategoryName }),
    ...payload
});

const prepareMixpanelViewPayload = ({
    pageName,
    subCategoryName,
    categoryName,
    nestedSubCategoryName,
    categoryIds,
    page,
    sort,
    filters,
    query,
    numberOfResults,
    hasFiverrChoiceGigs,
    fiverrChoiceGigPosition,
    promotedGigsCount,
    hasPromotedGigs,
    error,
    seenGuidedSearch,
    pro_only = false
}) => removeEmptyEntries({
    'Number Of Results': numberOfResults,
    'Query': query,
    'Sort': sort,
    'Applied Filters': filters,
    'Seen Fiverr Choice Badge': hasFiverrChoiceGigs,
    'Fiverr Choice Gig Position': fiverrChoiceGigPosition,
    'Number of Promoted Gigs': promotedGigsCount,
    'Seen Promoted Gigs Badge': hasPromotedGigs,
    'Page Number': page,
    'Is Pro Experience': pro_only,
    'V4 Perseus': true,
    'Error Type': error,
    'seen_guided_search': seenGuidedSearch,
    ...basePayload({ pageName, categoryIds, categoryName, subCategoryName, nestedSubCategoryName })
});

const prepareMixpanelFilterPayload = ({
    pageName,
    categoryIds,
    categoryName,
    subCategoryName,
    nestedSubCategoryName,
    id,
    value,
    location,
    filters,
    stepNum
}) => removeEmptyEntries({
    ...basePayload({ pageName, categoryIds, categoryName, subCategoryName, nestedSubCategoryName }),
    'Filter Name': id,
    'Filter Value': value,
    'Filter Location': location,
    'Applied filters': filters,
    'Step Number': stepNum
});

const prepareBiQueryFilterPayload = (reportData, type, id, value, location) => {

    const filter = {
        element: {
            metadata: id,
            text: value,
            position: location
        }
    };

    return removeEmptyEntries({
        type,
        group: 'listings_filters',
        listings: {
            ...reportData.listings
        },
        page: filter
    });
};

const baseTrackResults = (newValue, oldValue) => ({
    value: newValue ? newValue : oldValue,
    type: _findFilterType(newValue)
});

const seoBaseTrackResults = (newValue, oldValue) => ([{
    value: newValue ? newValue : oldValue,
    type: _findFilterType(newValue)
}]);

export const filterTrackResults = {
    [SELECTORS.types.SINGLE]: baseTrackResults,
    [SELECTORS.types.PAIR]: baseTrackResults,
    [SELECTORS.types.MULTI]: (newValue = [], oldValue = []) => {
        const added = newValue.length > oldValue.length;

        // Concat values and get unique ( We actually want the delta between the two )
        const values = [...newValue, ...oldValue];

        return {
            value: values.find((v) => values.filter((val) => val === v).length === 1),
            type: _findFilterType(added)
        };
    }
};

export const seoFilterTrackResults = {
    [SELECTORS.types.SINGLE]: seoBaseTrackResults,
    [SELECTORS.types.PAIR]: seoBaseTrackResults,
    [SELECTORS.types.MULTI]: (newValue = [], oldValue = []) => {
        const added = _.difference(newValue, oldValue);
        const removed = _.difference(oldValue, newValue);

        return added.map((value) => ({ value, type: EVENTS_MAPPING.FILTERS_APPLY })).concat(removed.map((value) => ({ value, type: EVENTS_MAPPING.FILTER_REMOVE })));
    }
};

class Tracker {

    initialize(context) {

        this.context = context;
    }

    track(type, payload, location) {
        const eventPayload = prepareMixpanelTrackPayload({ ...this.context, payload });
        const { reportData } = this.context;
        const { mixpanel, biquery } = type;
        const [id] = Object.keys(payload);

        mixpanel && mixpanelService.trackEvent(
            mixpanel,
            eventPayload
        );

        biquery && sendEvents(prepareBiQueryFilterPayload(reportData, biquery, id, payload[id], location));

        return eventPayload;
    }

    trackMobileFilterOpen() {
        const element = {
            name: FILTERS_LOCATION.FILTER_RESULTS,
            type: BI_CONSTS.BUTTON
        };

        this.trackBiClick(element);
    }

    trackFilterGroupOpen(name) {
        const element = {
            name,
            type: BI_CONSTS.BUTTON
        };

        this.trackBiClick(element);
    }

    trackBiClick(element) {
        const { reportData: { listings: { page_ctx_id } } } = this.context;

        sendEvents({
            ...BI_CLICK_DATA,
            page: {
                ctx_id: page_ctx_id,
                element
            }
        });
    }

    trackFilter(type, { id, value, location, stepNum } = {}) {
        const eventPayload = prepareMixpanelFilterPayload({ ...this.context, id, value, location, stepNum });
        const { reportData } = this.context;
        const { mixpanel, biquery } = type;

        mixpanel && mixpanelService.trackEvent(
            mixpanel,
            eventPayload
        );

        biquery && sendEvents(prepareBiQueryFilterPayload(reportData, biquery, id, value, location));

        return eventPayload;
    }

    trackSCFilter(type, { scId, alias, location }) {
        mixpanelService.trackEvent(
            type.mixpanel,
            { ...prepareMixpanelTrackPayload({ ...this.context }),
                'Filter Name': 'Sub Categories Filter',
                'Filter Value': alias,
                'Filter Location': location,
                'SC ID': scId
            }
        );
    }

    trackTileFilter(type, filterId, stepNum) {
        mixpanelService.trackEvent(
            type.mixpanel,
            prepareMixpanelTrackPayload({
                'Target': 'Top Filters Flow',
                'Sub Category Name': this.context.subCategoryName,
                'Step Number': stepNum,
                'Context': filterId,
                ...this.context
            })
        );
    }

    // Update the bigQuery params
    filterTrackingUpdate = (currentFilter, filterChange, location, id) => {

        const filterType = SELECTORS.getType(id);
        const { type, value } = filterTrackResults[filterType](filterChange[id], currentFilter[id]);

        this.trackFilter(type, { id, value, location });
    };

    seoFilterTrackingUpdate = (currentFilter, filterChange, location, id) => {

        const filterType = SELECTORS.getType(id);
        const toTrack = seoFilterTrackResults[filterType](filterChange[id], currentFilter[id]);

        toTrack.forEach(({ type, value }) => {
            this.trackFilter(type, { id, value, location });
        });
    };

    syncAndTrack({ filters, page, sort, reportData }, initialTime) {
        const load_time = new Date() - initialTime;
        const data = removeEmptyEntries({ ...reportData, page: { load_time } });

        this.context = {
            ...this.context,
            filters,
            page,
            sort,
            reportData
        };

        sendEvents(data);
    }

    view() {
        const eventPayload = prepareMixpanelViewPayload(this.context);
        const { reportData, localStorageData } = this.context;
        const { shouldStore, entrySuffix } = localStorageData;
        const loadTime = new Date() - new Date(reportData.page.v2_timestamp);
        mixpanelService.trackEvent(
            EVENTS_MAPPING.PAGE_VIEWED.mixpanel,
            eventPayload
        );

        shouldStore && localStorage.storePageView(entrySuffix);

        const data = removeEmptyEntries({ ...reportData, page: { load_time: loadTime } });
        sendEvents(data);
    }

    click(target, additionalArgs = {}) {
        const payload = { ...additionalArgs, target };
        this.track(EVENTS_MAPPING.CLICK, payload);
    }

    error() {
        const eventPayload = prepareMixpanelViewPayload(this.context);

        mixpanelService.trackEvent(
            EVENTS_MAPPING.PAGE_VIEWED.mixpanel,
            eventPayload
        );
    }

    trackEducationTooltipGotIt(tooltipName) {
        mixpanelService.trackEvent(
            EVENTS_MAPPING.CLICKED_GOT_IT.mixpanel,
            { 'Education tooltip': tooltipName }
        );
    }

    trackHowFiverrWorksVideo(payload) {
        mixpanelService.trackEvent(
            EVENTS_MAPPING.CLICK.TARGETS.HOW_FIVERR_WORKS_VIDEO,
            prepareMixpanelTrackPayload({ ...this.context, payload })
        );
    }

    trackGotoMarketPlace() {
        const { reportData: { listings: { page_ctx_id } } } = this.context;

        const data = {
            group: 'pro_navigation',
            type: EVENTS_MAPPING.MARKETPLACE_CLICK,
            listings: {
                is_pro: true,
                page_ctx_id
            }
        };

        sendEvents(data);
    }

    viewChoiceModalities() {
        const eventPayload = prepareMixpanelViewPayload(this.context);
        mixpanelService.trackEvent(
            EVENTS_MAPPING.VIEWED_CHOICE_MODALITIES.mixpanel,
            eventPayload
        );
    }

    clickChoiceModality({ bucketNumber, bucketData, gig }) {
        try {
            const eventPayload = {
                ...prepareMixpanelViewPayload(this.context),
                ...bucketData,
                'Bucket Number': bucketNumber,
                'Gig Id': gig.id,
                'Price': gig.price
            };

            mixpanelService.trackEvent(
                EVENTS_MAPPING.CLICKED_CHOICE_MODALITY.mixpanel,
                eventPayload
            );
        } catch (e) {
            logger.warn(e, { message: 'Failed to report Clicked Fiverr Choice Modality to MixPanel' });
        }
    }

    promotedAdsTooltipView() {
        try {
            const payload = prepareMixpanelViewPayload(this.context);
            mixpanelService.trackEvent(
                EVENTS_MAPPING.HOVER_PROMOTED_ADS_TOOLTIP.mixpanel,
                payload
            );
        } catch (e) {
            logger.warn(e, { message: `Failed to report ${EVENTS_MAPPING.HOVER_PROMOTED_ADS_TOOLTIP.mixpanel} event to MixPanel` });
        }
    }

    log(error, level = 'warn') {
        logger[level](error);
    }
}

const _findFilterType = (value) => value ? EVENTS_MAPPING.FILTERS_APPLY : EVENTS_MAPPING.FILTER_REMOVE;

export default new Tracker();
